import { useCallback, useMemo, useState } from 'react';
import {
  useCreateAssociatedPersonMutation,
  useDeleteAssociatedPersonMutation,
  useLazyGetAssociatedPersonsListQuery,
  useUpdateAssociatedPersonMutation,
} from 'services/merchants';
import { AssociatedPerson, CreateAssociatedPersonPayload, UpdateAssociatedPersonPayload } from 'services/merchants/types';

import { AssociatedPersonType, BridgeDocumentPurpose } from '../kyb/types';
import { parseFailedAttributes, parseSourceKey } from '../kyb/utils/mapBackendErrorsToFormFields';
import { ASSOCIATED_PERSONS_INITIAL_VALUES, AssociatedPersonFormType, AssociatedPersonsFormType } from './keys';

interface UseAssociatedPersonsApiResult {
  isLoading: boolean;
  isSubmitting: boolean;
  isDeleting: boolean;
  error: Record<string, string>;
  fetchAssociatedPersons: () => Promise<AssociatedPersonsFormType>;
  submitChanges: (
    formData: AssociatedPersonsFormType,
    originalData: AssociatedPersonsFormType,
  ) => Promise<{ success: boolean; requestId?: string }>;
  deletePersonById: (personUuid: string) => Promise<{ success: boolean }>;
  clearErrors: () => void;
}

const formatDateForFormNull = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
};

const formatDateForFormUndefined = (dateString: string | null | undefined): string | undefined => {
  if (!dateString) return undefined;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().split('T')[0];
};

const mapPersonToFormData = (person: AssociatedPerson): AssociatedPersonFormType => ({
  uuid: person.id,
  kycScreen: person.kycScreen || { screenedAt: '', result: 'passed' },
  ofacScreen: person.ofacScreen || { screenedAt: '', result: 'passed' },
  firstName: person.firstName || '',
  lastName: person.lastName || '',
  email: person.email || '',
  residentialAddress: {
    streetLine1: person.residentialAddress?.streetLine1 || '',
    streetLine2: person.residentialAddress?.streetLine2,
    city: person.residentialAddress?.city || '',
    subdivision: person.residentialAddress?.subdivision,
    postalCode: person.residentialAddress?.postalCode || '',
    country: person.residentialAddress?.country || '',
  },
  birthDate: formatDateForFormNull(person.birthDate),
  hasOwnership: person.hasOwnership ?? false,
  hasControl: person.hasControl ?? false,
  isSigner: person.isSigner ?? false,
  identifyingInformation: person.identifyingInformation?.map((info) => ({
    type: info.type || '',
    issuingCountry: info.issuingCountry || '',
    number: info.number || '',
    imageFront: info.imageFront,
  })) || [{ type: '', issuingCountry: '', number: '' }],
  documents: person.documents?.map((doc) => ({
    purposes: (doc.purposes || []) as BridgeDocumentPurpose[],
    file: doc.file,
    description: doc.description,
  })) || [{ purposes: [], file: undefined, description: undefined }],
  phone: person.phone,
  isDirector: person.isDirector ?? false,
  title: person.title || '',
  ownershipPercentage: person.ownershipPercentage ? Number(person.ownershipPercentage) : 0,
  relationshipEstablishedAt: formatDateForFormUndefined(person.relationshipEstablishedAt),
});

const mapTitanPersonToFormData = (person: AssociatedPersonType): AssociatedPersonFormType => ({
  uuid: undefined, // Titan data doesn't have id, will be created as new person
  kycScreen: { screenedAt: person.kycScreen?.screenedAt || '', result: 'passed' },
  ofacScreen: { screenedAt: person.ofacScreen?.screenedAt || '', result: 'passed' },
  firstName: person.firstName || '',
  lastName: person.lastName || '',
  email: person.email || '',
  residentialAddress: {
    streetLine1: person.residentialAddress?.streetLine1 || '',
    streetLine2: person.residentialAddress?.streetLine2,
    city: person.residentialAddress?.city || '',
    subdivision: person.residentialAddress?.subdivision,
    postalCode: person.residentialAddress?.postalCode || '',
    country: person.residentialAddress?.country || '',
  },
  birthDate: formatDateForFormNull(person.birthDate),
  hasOwnership: person.hasOwnership ?? false,
  hasControl: person.hasControl ?? false,
  isSigner: person.isSigner ?? false,
  identifyingInformation: person.identifyingInformation?.map((info) => ({
    type: info.type || '',
    issuingCountry: info.issuingCountry || '',
    number: info.number || '',
    imageFront: info.imageFront,
  })) || [{ type: '', issuingCountry: '', number: '' }],
  documents: person.documents?.map((doc) => ({
    purposes: (doc.purposes || []) as BridgeDocumentPurpose[],
    file: doc.file,
    description: doc.description,
  })) || [{ purposes: [], file: undefined, description: undefined }],
  phone: person.phone,
  isDirector: person.isDirector ?? false,
  title: person.title || '',
  ownershipPercentage: person.ownershipPercentage ? Number(person.ownershipPercentage) : 0,
  relationshipEstablishedAt: formatDateForFormUndefined(person.relationshipEstablishedAt),
});

const mapApiToFormData = (apiData: AssociatedPerson[]): AssociatedPersonsFormType => {
  if (!apiData || apiData.length === 0) {
    return { ...ASSOCIATED_PERSONS_INITIAL_VALUES };
  }

  return {
    beneficiaries: apiData.map(mapPersonToFormData),
  };
};

export const mapTitanToFormData = (apiData: AssociatedPersonType[]): AssociatedPersonsFormType | undefined => {
  if (!apiData || apiData.length === 0) {
    return undefined;
  }

  return {
    beneficiaries: apiData.map(mapTitanPersonToFormData),
  };
};

const fileToBase64 = async (file: File | string | undefined): Promise<string | undefined> => {
  if (!file) return undefined;
  if (typeof file === 'string') return file;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(new Error(`Failed to read file: ${error}`));
  });
};

const buildResidentialAddressPayload = (address: AssociatedPersonFormType['residentialAddress']) => {
  const payload: {
    streetLine1: string;
    streetLine2?: string;
    city: string;
    subdivision?: string;
    postalCode: string;
    country: string;
  } = {
    streetLine1: address.streetLine1,
    city: address.city,
    country: address.country,
    postalCode: address.postalCode,
  };

  if (address.streetLine2) {
    payload.streetLine2 = address.streetLine2;
  }
  if (address.subdivision) {
    payload.subdivision = address.subdivision;
  }

  return payload;
};

async function mapDocumentsForApi(
  documents: AssociatedPersonFormType['documents'],
): Promise<Array<{ purposes: string[]; file: string; description?: string }>> {
  return Promise.all(
    documents
      .filter((doc) => doc.file || (doc.purposes && doc.purposes.length > 0))
      .map(async (doc) => ({
        purposes: (doc.purposes || []) as string[],
        file: (await fileToBase64(doc.file)) || '',
        description: doc.description,
      })),
  );
}

const mapFormToApiPayload = async (
  formData: AssociatedPersonFormType,
): Promise<CreateAssociatedPersonPayload | UpdateAssociatedPersonPayload> => {
  const documents = await mapDocumentsForApi(formData.documents);

  const identifyingInformation = await Promise.all(
    formData.identifyingInformation
      .filter((info) => info.type || info.number)
      .map(async (info) => ({
        type: info.type,
        issuingCountry: info.issuingCountry,
        number: info.number,
        imageFront: await fileToBase64(info.imageFront as File | string | undefined),
      })),
  );

  const payload: any = {
    kycScreen: formData.kycScreen,
    ofacScreen: formData.ofacScreen,
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    residentialAddress: buildResidentialAddressPayload(formData.residentialAddress),
    birthDate: formData.birthDate || '',
    hasOwnership: formData.hasOwnership,
    hasControl: formData.hasControl,
    isSigner: formData.isSigner,
    identifyingInformation,
    isDirector: formData.isDirector,
    ownershipPercentage: formData.ownershipPercentage?.toString(),
  };

  if (formData.phone) {
    payload.phone = formData.phone;
  }
  if (formData.title) {
    payload.title = formData.title;
  }
  if (formData.relationshipEstablishedAt) {
    payload.relationshipEstablishedAt = formData.relationshipEstablishedAt;
  }
  if (documents.length > 0) {
    payload.documents = documents;
  }
  return payload;
};

const createPartialUpdatePayload = async (
  current: AssociatedPersonFormType,
  original: AssociatedPersonFormType,
): Promise<Partial<UpdateAssociatedPersonPayload>> => {
  const payload: Partial<UpdateAssociatedPersonPayload> = {};

  if (JSON.stringify(current.kycScreen) !== JSON.stringify(original.kycScreen)) {
    payload.kycScreen = current.kycScreen;
  }
  if (JSON.stringify(current.ofacScreen) !== JSON.stringify(original.ofacScreen)) {
    payload.ofacScreen = current.ofacScreen;
  }
  if (current.firstName !== original.firstName) {
    payload.firstName = current.firstName;
  }
  if (current.lastName !== original.lastName) {
    payload.lastName = current.lastName;
  }
  if (current.email !== original.email) {
    payload.email = current.email;
  }
  if (current.phone !== original.phone) {
    payload.phone = current.phone;
  }
  if (current.birthDate !== original.birthDate) {
    payload.birthDate = current.birthDate || '';
  }
  if (current.hasOwnership !== original.hasOwnership) {
    payload.hasOwnership = current.hasOwnership;
  }
  if (current.hasControl !== original.hasControl) {
    payload.hasControl = current.hasControl;
  }
  if (current.isSigner !== original.isSigner) {
    payload.isSigner = current.isSigner;
  }
  if (current.isDirector !== original.isDirector) {
    payload.isDirector = current.isDirector;
  }
  if (current.title !== original.title) {
    payload.title = current.title;
  }
  if (current.ownershipPercentage !== original.ownershipPercentage) {
    payload.ownershipPercentage = current.ownershipPercentage?.toString();
  }
  if (current.relationshipEstablishedAt !== original.relationshipEstablishedAt) {
    payload.relationshipEstablishedAt = current.relationshipEstablishedAt;
  }
  if (JSON.stringify(current.residentialAddress) !== JSON.stringify(original.residentialAddress)) {
    payload.residentialAddress = buildResidentialAddressPayload(current.residentialAddress);
  }
  if (JSON.stringify(current.identifyingInformation) !== JSON.stringify(original.identifyingInformation)) {
    payload.identifyingInformation = await Promise.all(
      current.identifyingInformation
        .filter((info) => info.type || info.number)
        .map(async (info) => ({
          type: info.type,
          issuingCountry: info.issuingCountry,
          number: info.number,
          imageFront: await fileToBase64(info.imageFront as File | string | undefined),
        })),
    );
  }
  if (JSON.stringify(current.documents) !== JSON.stringify(original.documents)) {
    const docs = await mapDocumentsForApi(current.documents);
    if (docs.length > 0) {
      payload.documents = docs;
    } else if ('documents' in payload) {
      delete payload.documents;
    }
  }

  return payload;
};

export function useAssociatedPersonsApi(merchantUuid?: string): UseAssociatedPersonsApiResult {
  const [error, setError] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [fetchPersonsList, { isLoading: isFetching }] = useLazyGetAssociatedPersonsListQuery();
  const [createPerson] = useCreateAssociatedPersonMutation();
  const [updatePerson] = useUpdateAssociatedPersonMutation();
  const [deletePerson] = useDeleteAssociatedPersonMutation();

  const clearErrors = useCallback(() => {
    setError({});
  }, []);

  const deletePersonById = useCallback(
    async (personUuid: string): Promise<{ success: boolean }> => {
      if (!merchantUuid || !personUuid) {
        return { success: false };
      }

      setIsDeleting(true);
      setError({});

      try {
        await deletePerson({ merchantUuid, personUuid }).unwrap();
        return { success: true };
      } catch (err) {
        const errorData = (err as { data?: { errorMessage?: string } })?.data;
        setError({ delete: errorData?.errorMessage || 'Failed to delete associated person' });
        return { success: false };
      } finally {
        setIsDeleting(false);
      }
    },
    [merchantUuid, deletePerson],
  );

  const fetchAssociatedPersons = useCallback(async (): Promise<AssociatedPersonsFormType> => {
    if (!merchantUuid) {
      return { ...ASSOCIATED_PERSONS_INITIAL_VALUES };
    }

    try {
      const result = await fetchPersonsList(merchantUuid).unwrap();
      return mapApiToFormData(result);
    } catch {
      return { ...ASSOCIATED_PERSONS_INITIAL_VALUES };
    }
  }, [merchantUuid, fetchPersonsList]);

  const submitChanges = useCallback(
    async (
      formData: AssociatedPersonsFormType,
      originalData: AssociatedPersonsFormType,
    ): Promise<{ success: boolean; requestId?: string }> => {
      if (!merchantUuid) {
        return { success: false };
      }

      setIsSubmitting(true);
      setError({});

      const errors: Record<string, string> = {};

      try {
        const originalUuids = new Set(originalData.beneficiaries.filter((b) => b.uuid).map((b) => b.uuid));

        const originalByUuid = new Map(originalData.beneficiaries.filter((b) => b.uuid).map((b) => [b.uuid, b]));

        const hasChanged = (current: AssociatedPersonFormType, original: AssociatedPersonFormType): boolean => {
          return (
            JSON.stringify(current.kycScreen) !== JSON.stringify(original.kycScreen) ||
            JSON.stringify(current.ofacScreen) !== JSON.stringify(original.ofacScreen) ||
            current.firstName !== original.firstName ||
            current.lastName !== original.lastName ||
            current.email !== original.email ||
            current.phone !== original.phone ||
            current.birthDate !== original.birthDate ||
            current.hasOwnership !== original.hasOwnership ||
            current.hasControl !== original.hasControl ||
            current.isSigner !== original.isSigner ||
            current.isDirector !== original.isDirector ||
            current.title !== original.title ||
            current.ownershipPercentage !== original.ownershipPercentage ||
            current.relationshipEstablishedAt !== original.relationshipEstablishedAt ||
            JSON.stringify(current.residentialAddress) !== JSON.stringify(original.residentialAddress) ||
            JSON.stringify(current.identifyingInformation) !== JSON.stringify(original.identifyingInformation) ||
            JSON.stringify(current.documents) !== JSON.stringify(original.documents)
          );
        };

        const toCreate = formData.beneficiaries.filter((b) => !b.uuid);
        const toUpdate = formData.beneficiaries.filter((b) => {
          if (!b.uuid || !originalUuids.has(b.uuid)) return false;
          const original = originalByUuid.get(b.uuid);
          return original && hasChanged(b, original);
        });

        const parseBackendErrors = (err: unknown, fieldPrefix: string) => {
          const errorData = (
            err as {
              data?: {
                meta?: {
                  failedAttributes?: Array<{ path: string; message: string }>;
                  source?: { key?: Record<string, string> };
                };
                errorMessage?: string;
              };
            }
          )?.data;

          if (errorData?.meta?.failedAttributes && errorData.meta.failedAttributes.length > 0) {
            Object.assign(errors, parseFailedAttributes(errorData.meta.failedAttributes, fieldPrefix));
          } else if (errorData?.meta?.source?.key && Object.keys(errorData.meta.source.key).length > 0) {
            Object.assign(errors, parseSourceKey(errorData.meta.source.key, fieldPrefix));
          } else if (errorData?.errorMessage) {
            errors[fieldPrefix] = errorData.errorMessage;
          } else {
            errors[fieldPrefix] = 'An error occurred';
          }
        };

        const createPayloads = await Promise.all(
          toCreate.map(async (person) => ({
            person,
            payload: (await mapFormToApiPayload(person)) as CreateAssociatedPersonPayload,
          })),
        );

        const updatePayloads = await Promise.all(
          toUpdate.map(async (person) => {
            const original = originalByUuid.get(person.uuid!);
            return {
              person,
              payload: await createPartialUpdatePayload(person, original!),
            };
          }),
        );

        const operations: Promise<unknown>[] = [];

        createPayloads.forEach(({ person, payload }) => {
          operations.push(
            createPerson({ merchantUuid, ...payload })
              .unwrap()
              .catch((err) => {
                const fieldPrefix = `beneficiaries[${formData.beneficiaries.indexOf(person)}]`;
                parseBackendErrors(err, fieldPrefix);
                throw err;
              }),
          );
        });

        updatePayloads.forEach(({ person, payload }) => {
          operations.push(
            updatePerson({ merchantUuid, personUuid: person.uuid!, ...payload })
              .unwrap()
              .catch((err) => {
                const fieldPrefix = `beneficiaries[${formData.beneficiaries.indexOf(person)}]`;
                parseBackendErrors(err, fieldPrefix);
                throw err;
              }),
          );
        });

        const results = await Promise.allSettled(operations);
        const hasFailures = results.some((r) => r.status === 'rejected');

        if (hasFailures) {
          setError(errors);
          return { success: false };
        }

        return { success: true, requestId: Date.now().toString() };
      } catch (err) {
        if (Object.keys(errors).length > 0) {
          setError(errors);
        }
        return { success: false };
      } finally {
        setIsSubmitting(false);
      }
    },
    [merchantUuid, createPerson, updatePerson],
  );

  return useMemo(
    () => ({
      isLoading: isFetching,
      isSubmitting,
      isDeleting,
      error,
      fetchAssociatedPersons,
      submitChanges,
      deletePersonById,
      clearErrors,
    }),
    [isFetching, isSubmitting, isDeleting, error, fetchAssociatedPersons, submitChanges, deletePersonById, clearErrors],
  );
}
